import React, { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { T, sx } from '../lib/tokens';
import { useIsMobile } from '../lib/useIsMobile';
import { Button } from '../components/ui/Button';
import { Upload as UploadIcon, Camera, Email, Check, Sparkle } from '../components/ui/Icons';

type DropState = 'idle' | 'processing' | 'done';

export function Upload() {
  const isMobile = useIsMobile();
  const [dropState, setDropState] = useState<DropState>('idle');
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inboxEmail, setInboxEmail] = useState('');
  const [uploadedId, setUploadedId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setInboxEmail('receipts@receiptrec.com');
  }, []);

  async function handleFile(file: File) {
    setDropState('processing');
    setError(null);
    try {
      const { receipt } = await api.uploadReceipt(file);
      setUploadedId(receipt.id);
      setDropState('done');
      setTimeout(() => {
        window.location.hash = `#/receipts/${receipt.id}`;
      }, 1200);
    } catch (err) {
      setError((err as Error).message);
      setDropState('idle');
    }
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) void handleFile(f);
  }

  function handleFileInput(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (f) void handleFile(f);
  }

  const dropZoneBorder = dragOver ? T.sage : T.paperLine;
  const dropZoneBg = dragOver ? 'rgba(168, 199, 158, 0.08)' : '#FFFFFF';

  if (isMobile) {
    return (
      <div style={{ padding: '0 16px' }}>
        <h1 style={{ ...sx.serif, fontSize: 28, color: T.graphite, marginBottom: 6 }}>
          Drop it. Done.
        </h1>
        <p style={{ fontSize: 14, color: T.graphiteDim, marginBottom: 24 }}>
          Upload a receipt and our AI will extract, categorise, and post it.
        </p>

        {/* Camera button - prominent full width */}
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            width: '100%',
            padding: '16px 0',
            background: T.navy,
            color: T.cream,
            borderRadius: 14,
            fontSize: 16,
            fontWeight: 600,
            fontFamily: "'Geist', sans-serif",
            cursor: 'pointer',
            marginBottom: 12,
            minHeight: 56,
          }}
        >
          <Camera size={22} color={T.cream} />
          Take a photo
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: 'none' }}
            onChange={handleFileInput}
          />
        </label>

        {/* Choose file button */}
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            width: '100%',
            padding: '14px 0',
            background: '#FFFFFF',
            color: T.graphite,
            border: `1px solid ${T.paperLine}`,
            borderRadius: 14,
            fontSize: 14,
            fontWeight: 500,
            fontFamily: "'Geist', sans-serif",
            cursor: dropState === 'idle' ? 'pointer' : 'default',
            marginBottom: 12,
            minHeight: 48,
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,application/pdf"
            style={{ display: 'none' }}
            onChange={handleFileInput}
          />
          {dropState === 'idle' && (
            <>
              <UploadIcon size={18} color={T.graphiteMuted} />
              Choose file
            </>
          )}
          {dropState === 'processing' && (
            <>
              <Sparkle size={18} color={T.sage} />
              AI is extracting your data...
            </>
          )}
          {dropState === 'done' && (
            <>
              <Check size={18} color={T.sageDarker} />
              Done! Redirecting...
            </>
          )}
        </label>

        {/* Email forward compact row */}
        {inboxEmail && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '12px 14px',
              background: '#FFFFFF',
              border: `1px solid ${T.paperLine}`,
              borderRadius: 14,
              marginBottom: 12,
            }}
          >
            <Email size={16} color={T.graphiteDim} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: T.graphite, marginBottom: 2 }}>
                Email forward
              </div>
              <code
                style={{
                  ...sx.mono,
                  fontSize: 11,
                  color: T.graphiteDim,
                  wordBreak: 'break-all',
                }}
              >
                {inboxEmail}
              </code>
            </div>
          </div>
        )}

        {error && (
          <div
            style={{
              padding: '12px 16px',
              background: T.failBg,
              borderRadius: 10,
              fontSize: 13,
              color: T.fail,
              lineHeight: 1.5,
            }}
          >
            {error}
          </div>
        )}

        {/* Safe area padding for iOS */}
        <div style={{ height: 'env(safe-area-inset-bottom, 20px)' }} />
      </div>
    );
  }

  return (
    <div>
      <h1 style={{ ...sx.serif, fontSize: 36, color: T.graphite, marginBottom: 8 }}>
        Drop it. Done.
      </h1>
      <p style={{ fontSize: 14, color: T.graphiteDim, marginBottom: 32 }}>
        Upload a receipt and our AI will extract, categorise, and post it.
      </p>

      {/* 3-column layout */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gap: 16,
          marginBottom: 24,
        }}
      >
        {/* Big drag-drop zone */}
        <label
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          style={{
            gridColumn: '1 / 2',
            gridRow: '1 / 3',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 16,
            padding: 40,
            border: `2px dashed ${dropZoneBorder}`,
            borderRadius: 16,
            background: dropZoneBg,
            cursor: dropState === 'idle' ? 'pointer' : 'default',
            transition: 'border-color 200ms ease, background 200ms ease',
            minHeight: 260,
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,application/pdf"
            style={{ display: 'none' }}
            onChange={handleFileInput}
          />

          {dropState === 'idle' && (
            <>
              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: 14,
                  background: T.paper2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <UploadIcon size={24} color={T.graphiteMuted} />
              </div>
              <div style={{ textAlign: 'center' }}>
                <p style={{ fontSize: 14, fontWeight: 500, color: T.graphite, marginBottom: 4 }}>
                  Drag & drop receipt
                </p>
                <p style={{ fontSize: 13, color: T.graphiteDim }}>
                  or click to choose a file
                </p>
              </div>
            </>
          )}

          {dropState === 'processing' && (
            <>
              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: 14,
                  background: T.navy,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  animation: 'spin 2s linear infinite',
                }}
              >
                <Sparkle size={24} color={T.sage} />
              </div>
              <p style={{ fontSize: 14, fontWeight: 500, color: T.graphite }}>
                AI is extracting your data...
              </p>
              <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
            </>
          )}

          {dropState === 'done' && (
            <>
              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: 14,
                  background: T.sage,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Check size={28} color={T.navy} />
              </div>
              <p style={{ fontSize: 14, fontWeight: 500, color: T.sageDarker }}>
                Done! Redirecting...
              </p>
            </>
          )}
        </label>

        {/* Email forward card */}
        <div
          style={{
            background: '#FFFFFF',
            border: `1px solid ${T.paperLine}`,
            borderRadius: 16,
            padding: 24,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              background: T.paper2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Email size={20} color={T.graphiteMuted} />
          </div>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: T.graphite, margin: 0 }}>
            Email forward
          </h3>
          <p style={{ fontSize: 13, color: T.graphiteDim, lineHeight: 1.5 }}>
            Forward receipt emails directly and they get processed automatically.
          </p>
          {inboxEmail && (
            <code
              style={{
                ...sx.mono,
                fontSize: 12,
                color: T.graphite,
                background: T.paper2,
                padding: '6px 10px',
                borderRadius: 6,
                wordBreak: 'break-all',
              }}
            >
              {inboxEmail}
            </code>
          )}
        </div>

        {/* Camera card */}
        <div
          style={{
            background: '#FFFFFF',
            border: `1px solid ${T.paperLine}`,
            borderRadius: 16,
            padding: 24,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              background: T.paper2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Camera size={20} color={T.graphiteMuted} />
          </div>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: T.graphite, margin: 0 }}>
            Snap a photo
          </h3>
          <p style={{ fontSize: 13, color: T.graphiteDim, lineHeight: 1.5 }}>
            Use your phone camera to capture a receipt on the go.
          </p>
          <label
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              padding: '10px 20px',
              background: T.navy,
              color: T.cream,
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 500,
              fontFamily: "'Geist', sans-serif",
              cursor: 'pointer',
              marginTop: 'auto',
            }}
          >
            <Camera size={15} color={T.cream} />
            Take photo
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: 'none' }}
              onChange={handleFileInput}
            />
          </label>
        </div>
      </div>

      {error && (
        <div
          style={{
            padding: '12px 16px',
            background: T.failBg,
            borderRadius: 10,
            fontSize: 13,
            color: T.fail,
            lineHeight: 1.5,
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
}
